import Fuse, { type IFuseOptions } from 'fuse.js/basic';
import type { AccountOption, AccountSearchEntry } from './account-option';

export interface AccountOptionSearchResult {
  /** The options the player can act on, best match first. */
  options: AccountOption[];
  /** For a guest whose best match needs an account: completes "Sign in to …". */
  signInTo?: string;
}

const FUSE_OPTIONS: IFuseOptions<AccountSearchEntry> = {
  keys: [
    { name: 'option.label', weight: 3 },
    { name: 'option.synonyms', weight: 2 },
    { name: 'option.section', weight: 1 },
  ],
  // Loose enough for a typo ("keybord"); MAX_SCORE then drops the weak tail Fuse keeps across several keys.
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
};

/** 0 is exact, 1 no match. Above this a result is noise, e.g. "acount" against Keyboard layout. */
const MAX_SCORE = 0.5;

/** Fuzzy matches `query` against each option's label, synonyms and section. An empty query matches nothing. */
export const searchAccountOptions = (
  entries: readonly AccountSearchEntry[],
  query: string,
): AccountOptionSearchResult => {
  const trimmed = query.trim();
  if (!trimmed) return { options: [] };

  const matches = new Fuse(entries, FUSE_OPTIONS)
    .search(trimmed)
    .filter(({ score = 1 }) => score <= MAX_SCORE)
    .map(({ item }) => item);

  return {
    options: matches.filter(({ usable }) => usable).map(({ option }) => option),
    signInTo: matches.find(({ usable, option }) => !usable && option.signInTo)?.option.signInTo,
  };
};
